import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import HeroSection from '../../sections/HeroSection/HeroSection';
import styles from './ContactPage.module.css';

const SOCIALS = [
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/berlibrewingco/',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
      </svg>
    ),
  },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/people/Berli-Brewing-Co/61590093867493/',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
      </svg>
    ),
  },
];

const ENQUIRY_TYPES = ['General Enquiry', 'Feedback', 'Event Enquiry', 'Press / Media', 'Careers', 'Other'];

const INITIAL = { name: '', email: '', enquiryType: '', message: '' };

const ContactPage = () => {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState('idle');
  const [touched, setTouched] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => setTouched(prev => ({ ...prev, [e.target.name]: true }));

  const isValid = form.name.trim() && form.email.includes('@') && form.message.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) { setTouched({ name: true, email: true, message: true }); return; }
    setStatus('sending');
    try {
      await addDoc(collection(db, 'contact-enquiries'), {
        ...form, submittedAt: serverTimestamp(),
      });

      fetch(process.env.REACT_APP_SHEETS_WEBHOOK, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'contact', ...form }),
      }).catch(err => console.error('Sheets webhook failed:', err));

      fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      }).catch(err => console.error('Email notification failed:', err));

      setStatus('success');
      setForm(INITIAL);
      setTouched({});
    } catch (err) {
      console.error('Submission error:', err);
      setStatus('error');
    }
  };

  const err = (field) => touched[field] && !form[field].trim();
  const emailErr = touched.email && !form.email.includes('@');

  return (
    <>
      <HeroSection
        title="Come Find Us"
        image="/images/28-DSC09439.jpg"
        imageAlt="Berli Brewing Co."
      />

      <section className={styles.contactSection}>
        <div className={styles.contactContainer}>

          {/* ── LEFT — Info ── */}
          <aside className={styles.infoPanel}>
            <span className={styles.eyebrowLight}>Find Us</span>
            <h2 className={styles.infoHeading}>
              Come for the beer.<br />Stay for the feeling.
            </h2>
            <p className={styles.infoIntro}>
              Have a question, a story to share, or just want to say hello? We're always happy to hear from you — the door is open.
            </p>

            {/* ── REDESIGNED CLEAN INFO LIST ── */}
            <div className={styles.infoList}>
              
              {/* Address */}
              <div className={styles.infoItem}>
                <h4 className={styles.infoLabel}>Address</h4>
                <p className={styles.infoText}>
                  Berli Brewing Co.<br />
                  11th Floor, Gera Imperium Alpha,<br />
                  Rajaram Patil Nagar, Vitthal Nagar, Kharadi,<br />
                  Pune, Maharashtra 411014
                </p>
                <a href="https://maps.app.goo.gl/HmGcy2Av9gVndRkj9" target="_blank" rel="noopener noreferrer" className={styles.infoLink}>
                  View on Map →
                </a>
              </div>

              {/* Contact (Phone & Email grouped) */}
              <div className={styles.infoItem}>
                <h4 className={styles.infoLabel}>Contact</h4>
                <div className={styles.contactSplit}>
                  <div className={styles.splitBlock}>
                    <p className={styles.infoText}>+91 74200 01640</p>
                    <a href="tel:+917420001640" className={styles.infoLink}>Call Us →</a>
                  </div>
                  <div className={styles.splitBlock}>
                    <p className={styles.infoText}>contact@berlibrewing.com</p>
                    <a href="mailto:contact@berlibrewing.com" className={styles.infoLink}>Send Email →</a>
                  </div>
                </div>
              </div>

              {/* Updated Hours */}
              <div className={styles.infoItem}>
                <h4 className={styles.infoLabel}>Opening Hours</h4>
                <p className={styles.infoText}>
                  <strong className={styles.dayLabel}>Tue – Sun:</strong> 1:00 PM – 1:30 AM<br />
                  <strong className={styles.dayLabel}>Monday:</strong> 6:00 PM – 1:30 AM (Dinner only)
                </p>
              </div>

            </div>

            <hr className={styles.divider} />

            {/* CTA & Socials aligned neatly at the bottom */}
            <div className={styles.bottomActions}>
              <a href="/reservation" className={styles.resCtaBtn}>Reserve a Table</a>
              
              <div className={styles.socialsRow}>
                {SOCIALS.map(({ label, href, icon }) => (
                  <a key={label} href={href} target="_blank" rel="noopener noreferrer"
                    className={styles.socialBtn} aria-label={label}>
                    <span className={styles.socialIcon}>{icon}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className={styles.infoPanelGrain} aria-hidden="true" />
          </aside>

          {/* ── RIGHT — Form ── */}
          <div className={styles.formPanel}>
            <span className={styles.eyebrowDark}>Send a Message</span>
            <h2 className={styles.formHeading}>We'd love to<br />hear from you.</h2>

            {status === 'success' ? (
              <div className={styles.successState}>
                <span className={styles.successIcon}>✦</span>
                <h3 className={styles.successTitle}>Message Received</h3>
                <p className={styles.successText}>
                  Thanks for reaching out. We'll get back to you within 24 hours — see you soon.
                </p>
                <button className={styles.resetBtn} onClick={() => setStatus('idle')}>
                  Send Another
                </button>
              </div>
            ) : (
              <form className={styles.form} onSubmit={handleSubmit} noValidate>

                <div className={styles.formRow}>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor="name">
                      Full Name <span className={styles.req}>*</span>
                    </label>
                    <input id="name" name="name" type="text"
                      className={`${styles.input} ${err('name') ? styles.inputError : ''}`}
                      placeholder="Your name"
                      value={form.name} onChange={handleChange} onBlur={handleBlur} />
                    {err('name') && <span className={styles.errorMsg}>Name is required</span>}
                  </div>

                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor="email">
                      Email <span className={styles.req}>*</span>
                    </label>
                    <input id="email" name="email" type="email"
                      className={`${styles.input} ${emailErr ? styles.inputError : ''}`}
                      placeholder="you@example.com"
                      value={form.email} onChange={handleChange} onBlur={handleBlur} />
                    {emailErr && <span className={styles.errorMsg}>Valid email required</span>}
                  </div>
                </div>

                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>Type of Enquiry</label>
                  <div className={styles.chipRow}>
                    {ENQUIRY_TYPES.map(type => (
                      <button
                        key={type}
                        type="button"
                        className={`${styles.chip} ${form.enquiryType === type ? styles.chipActive : ''}`}
                        onClick={() => setForm(p => ({ ...p, enquiryType: type }))}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel} htmlFor="message">
                    Message <span className={styles.req}>*</span>
                  </label>
                  <textarea id="message" name="message" rows={6}
                    className={`${styles.input} ${styles.textarea} ${err('message') ? styles.inputError : ''}`}
                    placeholder="What's on your mind?"
                    value={form.message} onChange={handleChange} onBlur={handleBlur} />
                  {err('message') && <span className={styles.errorMsg}>Message is required</span>}
                </div>

                {status === 'error' && (
                  <p className={styles.submitError}>Something went wrong. Please try again.</p>
                )}

                <button type="submit" className={styles.submitBtn} disabled={status === 'sending'}>
                  {status === 'sending' ? <span className={styles.spinner} /> : 'Send Message'}
                </button>

              </form>
            )}
          </div>

        </div>

        {/* Map */}
        <div className={styles.mapWrapper}>
          <iframe
            title="Berli Brewing Co. Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3782.381565467204!2d73.95244877465299!3d18.5568286680668!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2c376ad3dfd1d%3A0x2c7619d28c7924bd!2sBerli%20Brewing%20Co.%20(Tiny%20Rebel%20Brewing%20Private%20Limited)!5e0!3m2!1sen!2sin!4v1780570140837!5m2!1sen!2sin"
            width="100%" height="100%" style={{ border: 0 }}
            allowFullScreen loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>

      </section>
    </>
  );
};

export default ContactPage;